import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd"
import { useState } from "react"
import { Link } from "react-router-dom"
import type { PhysicalIdentity } from "../../types/physical"
import { physicalApi, physicalError } from "../../utils/physicalApi"
import {
  buildEquipmentImport,
  initialImportDraft,
  proposeImportComponents,
  proposeImportEquipment,
  validateEquipmentImport,
} from "../../utils/physicalImport"
import type {
  EquipmentImportDraft,
  EquipmentImportPreview,
  EquipmentImportResult,
  ImportEquipment,
  NewImportAsset,
  NewImportComponent,
} from "../../utils/physicalImport"
import { usePhysicalResource } from "./usePhysicalResource"
export function EquipmentImport({
  site,
  onCancel,
  onImported,
}: {
  site: PhysicalIdentity
  onCancel: () => void
  onImported: (result: EquipmentImportResult) => void
}) {
  const [applying, setApplying] = useState(false)
  const resource = usePhysicalResource(`equipment-import/${site.uuid}`, (signal) =>
    physicalApi.importPreview(site.uuid, signal),
  )
  return (
    <Modal
      open
      width={1200}
      className="physical-import-modal"
      title="Importer l’inventaire depuis les inspections"
      onCancel={applying ? undefined : onCancel}
      closable={!applying}
      keyboard={!applying}
      maskClosable={false}
      footer={null}
    >
      {resource.error && (
        <Alert
          type="error"
          message={physicalError(resource.error)}
          action={<Button onClick={resource.reload}>Réessayer</Button>}
        />
      )}
      {resource.loading && <Spin />}
      {resource.data && (
        <ImportReview
          key={resource.data.token}
          preview={resource.data}
          onCancel={onCancel}
          onImported={onImported}
          reload={resource.reload}
          onBusyChange={setApplying}
        />
      )}
    </Modal>
  )
}
function ImportReview({
  preview,
  onCancel,
  onImported,
  reload,
  onBusyChange,
}: {
  preview: EquipmentImportPreview
  onCancel: () => void
  onImported: (result: EquipmentImportResult) => void
  reload: () => void
  onBusyChange: (busy: boolean) => void
}) {
  const [draft, setDraft] = useState(() => initialImportDraft(preview)),
    [reason, setReason] = useState("Import de l’inventaire après revue des inspections rattachées"),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string>()
  const plan = buildEquipmentImport(preview, draft, reason, confirmed),
    errors = validateEquipmentImport(preview, draft)
  const change = (next: EquipmentImportDraft) => {
    setDraft(next)
    setConfirmed(false)
    setError(undefined)
  }
  const sourceLabels = new Map(
    preview.equipment.map((e, i) => [e.uuid, `relevé ${i + 1} · ${e.inspectionName}`]),
  )
  const selectedGroups = [
    ...new Set(
      Object.values(draft.targets)
        .filter((v) => v.startsWith("new:"))
        .map((v) => v.slice(4)),
    ),
  ]
  const setTarget = (e: ImportEquipment, value: string) => {
    const components = { ...draft.components }
    for (const c of e.contexts) if (!c.physicalComponentUuid) components[c.uuid] = ""
    change({ ...draft, targets: { ...draft.targets, [e.uuid]: value }, components })
  }
  const submit = async () => {
    if (!confirmed || errors.length || !plan.equipment.length || !reason.trim()) return
    setBusy(true)
    onBusyChange(true)
    setError(undefined)
    try {
      const result = await physicalApi.importEquipment(preview.site.uuid, plan)
      setBusy(false)
      onBusyChange(false)
      onImported(result)
    } catch (e) {
      setError(physicalError(e))
      setBusy(false)
      onBusyChange(false)
    }
  }
  const componentChoices = (
    e: ImportEquipment,
    c: { uuid: string; label: string; physicalComponentUuid: string | null },
  ) => {
    if (c.physicalComponentUuid)
      return (
        <Link
          to={`/physical-components/${c.physicalComponentUuid}`}
          target="_blank"
          rel="noreferrer"
        >
          Déjà rattaché :{" "}
          {preview.components.find((x) => x.uuid === c.physicalComponentUuid)?.name ?? c.label}
        </Link>
      )
    const parent = draft.targets[e.uuid]
    const shared = new Set([
      c.uuid,
      ...preview.equipment
        .filter((row) => draft.targets[row.uuid] === parent)
        .flatMap((row) =>
          row.contexts
            .map((ctx) => draft.components[ctx.uuid] ?? "")
            .filter((x) => x.startsWith("new:"))
            .map((x) => x.slice(4)),
        ),
    ])
    const options = [
      { value: "", label: "Ne pas importer ce composant" },
      ...preview.components
        .filter((x) => !x.archived && parent === `asset:${x.physicalAssetUuid}`)
        .map((x) => ({
          value: `component:${x.uuid}`,
          label: `Existant : ${x.name}${x.code ? ` · ${x.code}` : ""}`,
        })),
      ...[...shared].map((key) => ({
        value: `new:${key}`,
        label: `${key === c.uuid ? "Créer" : "Même nouveau composant"} : ${draft.newComponents[key]?.name ?? key}`,
      })),
    ]
    return (
      <Select
        aria-label={`Importer le composant ${c.label} de ${e.uuid}`}
        style={{ width: "100%", minWidth: 220 }}
        disabled={busy || !parent}
        value={draft.components[c.uuid] ?? ""}
        options={options}
        showSearch
        optionFilterProp="label"
        onChange={(v) => change({ ...draft, components: { ...draft.components, [c.uuid]: v } })}
      />
    )
  }
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Vérifiez quels relevés représentent réellement le même équipement."
        description="Une référence peut désigner un modèle présent plusieurs fois sur le site. Les noms et les modèles réglementaires ne prouvent pas une identité physique. Les liens déjà confirmés sont conservés ; les réponses, photos et anciens rapports restent inchangés."
      />
      <Space wrap>
        <Button disabled={busy} onClick={() => change(proposeImportEquipment(preview, draft))}>
          Proposer les regroupements d’équipements
        </Button>
        <Button disabled={busy} onClick={() => change(proposeImportComponents(preview, draft))}>
          Proposer les composants par libellé — à vérifier
        </Button>
        <Popconfirm title="Recharger les données et abandonner cet aperçu ?" onConfirm={reload}>
          <Button disabled={busy}>Actualiser l’aperçu</Button>
        </Popconfirm>
      </Space>
      {error && <Alert type="error" showIcon message={error} />}
      {errors.map((e) => (
        <Alert key={e} type="warning" message={e} />
      ))}
      <Tabs
        items={[
          {
            key: "sources",
            label: `Relevés d’inspection (${preview.equipment.length})`,
            children: (
              <Table<ImportEquipment>
                rowKey="uuid"
                dataSource={preview.equipment}
                pagination={{ pageSize: 15, showSizeChanger: false }}
                scroll={{ x: 850 }}
                columns={[
                  {
                    title: "Inspection",
                    key: "inspection",
                    render: (_, e) => (
                      <Space direction="vertical" size={0}>
                        <Link to={`/sites/${e.inspectionUuid}`} target="_blank" rel="noreferrer">
                          {e.inspectionName || "Inspection"}
                        </Link>
                        <Typography.Text type="secondary">
                          {e.executionDate
                            ? new Date(e.executionDate).toLocaleDateString("fr-FR")
                            : "Sans date"}
                        </Typography.Text>
                      </Space>
                    ),
                  },
                  {
                    title: "Équipement enregistré",
                    key: "source",
                    render: (_, e) => (
                      <Space direction="vertical" size={0}>
                        <strong>{e.name || "Sans nom"}</strong>
                        <span>
                          {[e.manufacturer, e.reference].filter(Boolean).join(" · ") ||
                            "Sans référence ni fabricant"}
                        </span>
                        {e.matches.some((m) => !m.strong) && (
                          <Typography.Text type="secondary">
                            Nom similaire : vérification manuelle
                          </Typography.Text>
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: "Équipement physique cible",
                    key: "target",
                    render: (_, e) =>
                      e.physicalAssetUuid ? (
                        <Link
                          to={`/physical-assets/${e.physicalAssetUuid}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Déjà rattaché :{" "}
                          {preview.assets.find((a) => a.uuid === e.physicalAssetUuid)?.name ??
                            "Équipement"}
                        </Link>
                      ) : (
                        <Select
                          aria-label={`Importer l’équipement ${e.uuid}`}
                          style={{ width: "100%", minWidth: 280 }}
                          disabled={busy}
                          value={draft.targets[e.uuid] ?? ""}
                          showSearch
                          optionFilterProp="label"
                          onChange={(v) => setTarget(e, v)}
                          options={[
                            { value: "", label: "Ne pas importer ce relevé" },
                            ...preview.assets
                              .filter((a) => !a.archived)
                              .map((a) => ({
                                value: `asset:${a.uuid}`,
                                label: `Existant : ${a.name}${a.code ? ` · ${a.code}` : ""}${e.matches.find((m) => m.uuid === a.uuid)?.strong ? " · réf./fabricant concordants" : ""}`,
                              })),
                            ...[
                              ...new Set([
                                e.uuid,
                                ...(e.suggestedGroup ? [e.suggestedGroup] : []),
                                ...selectedGroups,
                              ]),
                            ].map((key) => ({
                              value: `new:${key}`,
                              label: `${key === e.uuid ? "Créer" : "Même nouvel équipement"} : ${draft.assets[key]?.name ?? key} (${sourceLabels.get(key) ?? key})`,
                            })),
                          ]}
                        />
                      ),
                  },
                ]}
                expandable={{
                  rowExpandable: (e) => e.contexts.length > 0,
                  expandedRowRender: (e) => (
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {e.contexts.map((c) => (
                        <div className="physical-link-field" key={c.uuid}>
                          <strong>{c.label}</strong>
                          {componentChoices(e, c)}
                        </div>
                      ))}
                    </Space>
                  ),
                }}
                locale={{ emptyText: "Aucun équipement dans les inspections rattachées." }}
              />
            ),
          },
          {
            key: "assets",
            label: `Nouveaux équipements (${plan.newAssets.length})`,
            children: (
              <Table<NewImportAsset>
                rowKey="key"
                dataSource={plan.newAssets}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                scroll={{ x: 800 }}
                columns={(
                  [
                    ["name", "Nom"],
                    ["code", "Code interne (facultatif)"],
                    ["manufacturer", "Fabricant"],
                    ["model", "Modèle / référence"],
                    ["serialNumber", "Numéro de série"],
                  ] as [keyof NewImportAsset, string][]
                ).map(([key, title]) => ({
                  title,
                  key,
                  render: (_, asset) => (
                    <Input
                      aria-label={`${title} du nouvel équipement ${asset.key}`}
                      maxLength={key === "code" ? 100 : 250}
                      value={asset[key]}
                      disabled={busy}
                      onChange={(e) =>
                        change({
                          ...draft,
                          assets: {
                            ...draft.assets,
                            [asset.key]: { ...asset, [key]: e.target.value },
                          },
                        })
                      }
                    />
                  ),
                }))}
              />
            ),
          },
          {
            key: "components",
            label: `Nouveaux composants (${plan.newComponents.length})`,
            children: (
              <Table<NewImportComponent>
                rowKey="key"
                dataSource={plan.newComponents}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                columns={[
                  {
                    title: "Nom",
                    key: "name",
                    render: (_, component) => (
                      <Input
                        aria-label={`Nom du nouveau composant ${component.key}`}
                        maxLength={250}
                        value={component.name}
                        disabled={busy}
                        onChange={(e) =>
                          change({
                            ...draft,
                            newComponents: {
                              ...draft.newComponents,
                              [component.key]: { ...component, name: e.target.value },
                            },
                          })
                        }
                      />
                    ),
                  },
                  {
                    title: "Code interne (facultatif)",
                    key: "code",
                    render: (_, component) => (
                      <Input
                        maxLength={100}
                        value={component.code}
                        disabled={busy}
                        onChange={(e) =>
                          change({
                            ...draft,
                            newComponents: {
                              ...draft.newComponents,
                              [component.key]: { ...component, code: e.target.value },
                            },
                          })
                        }
                      />
                    ),
                  },
                ]}
              />
            ),
          },
        ]}
      />
      <Typography.Text>
        {
          plan.equipment.filter(
            (e) =>
              !preview.equipment.find((source) => source.uuid === e.equipmentUuid)
                ?.physicalAssetUuid,
          ).length
        }{" "}
        équipement(s) à rattacher · {plan.equipment.reduce((n, e) => n + e.components.length, 0)}{" "}
        composant(s) à rattacher · {plan.newAssets.length} équipement(s) et{" "}
        {plan.newComponents.length} composant(s) à créer.
      </Typography.Text>
      <label className="physical-link-field">
        <span>Motif de l’import *</span>
        <Input.TextArea
          aria-label="Motif de l’import"
          rows={2}
          value={reason}
          maxLength={2000}
          disabled={busy}
          onChange={(e) => {
            setReason(e.target.value)
            setConfirmed(false)
          }}
        />
      </label>
      <Checkbox
        checked={confirmed}
        disabled={busy}
        onChange={(e) => setConfirmed(e.target.checked)}
      >
        J’ai vérifié les regroupements, les composants et les informations à créer.
      </Checkbox>
      <Space>
        <Button
          type="primary"
          loading={busy}
          disabled={
            !confirmed ||
            !!errors.length ||
            !plan.equipment.length ||
            !reason.trim() ||
            preview.site.archived
          }
          onClick={submit}
        >
          Confirmer l’import
        </Button>
        <Button disabled={busy} onClick={onCancel}>
          Fermer
        </Button>
      </Space>
    </Space>
  )
}
